import { useEffect, useState, useRef } from "react";
import { uniqueId } from "lodash";
import useDebounce from "./useDebounce";
import handleWriteShift, {
  FeedbackWithRange,
  WriteShiftRequest,
  writeshiftCheck,
} from "./writeshift/WriteShift";
import { useKeycloak } from "@react-keycloak/web/lib/useKeycloak";

/**
 * A React hook to get feedback from WriteShift based on the html string passed in.
 *
 * Debounces using the debounceMS.
 *
 * @param editorContent the outerhtml of the text editor node, or the text of a text area.
 * @param debounceMS milliseconds to debounce getting the feedback
 * @param shouldRefresh whether or not feedback should be refreshed
 * @returns Feedback from the server based on that text, and a boolean if the latest request is loading.
 */
function useFeedback(
  editorContent: string,
  debounceMS: number,
  shouldRefresh: boolean
): {
  feedback: FeedbackWithRange[];
  isLoading: boolean;
  docId: string | undefined;
  scoreId: string | undefined;
} {
  const [feedback, setFeedback] = useState<FeedbackWithRange[]>([]);
  const latestRequest = useRef<string>();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // keeps track of the document id returned back from the first check,
  // to keep continuity between feedback checking
  const [docId, setDocId] = useState<string | undefined>();
  const [scoreId, setScoreId] = useState<string | undefined>();
  const debouncedContent = useDebounce(editorContent, debounceMS);
  const [prevContent, setPrevContent] = useState<string | undefined>();

  // TODO: this is an akward inclusion. We just want to refetch when the language changes,
  // because the grammar client already updates its language setting the language changes in extension storage.
  // But in order for the effect to run, we need to make it a dependency...
  const [language] = ["ENGLISH"];
  const { keycloak } = useKeycloak();

  useEffect(() => {
    // we don't care about empty, no checks to make :)
    if (
      debouncedContent === "" ||
      debouncedContent === prevContent ||
      !language
    ) {
      return;
    }
    // IIFE to get async-await
    (async () => {
      const currentRequest = uniqueId();
      latestRequest.current = currentRequest;
      setIsLoading(true);

      const accessToken = keycloak?.token;
      const req: WriteShiftRequest = {
        text: debouncedContent,
        docId: "docId",
        language: "ENGLISH",
      };
      // const response = await writeshiftCheck(debouncedContent, language, docId);
      const response = await handleWriteShift(req, accessToken!);

      // ignore outdated responses (the offsets won't match)
      const responseIsOutdated = latestRequest.current !== currentRequest;
      if (responseIsOutdated) {
        console.warn("[Outdated response]", response);
        setIsLoading(false);
        return;
      }

      const { completed, score } = response;

      // TODO(@tmagnusson): possibly return the error to let front end
      // know something's wrong.
      if (completed && score) {
        const list = score.feedbackList || [];
        setFeedback(list);
        setDocId(response.docId || undefined);
        setScoreId(response.scoreId);
        setPrevContent(debouncedContent);
      }
      setIsLoading(false);
    })();
  }, [debouncedContent, docId, language]);

  return { feedback, isLoading, docId, scoreId };
}

export default useFeedback;
